import { useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";

const ModalDocumentosAdjuntos = ({ open, handleClose, adjuntosJson, loading }) => {
  const theme = useTheme();

  const documentos = useMemo(() => {
    if (!adjuntosJson) return [];
    try {
      const parsed = typeof adjuntosJson === "string" ? JSON.parse(adjuntosJson) : adjuntosJson;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [adjuntosJson]);

  const base64ToBlob = (base64, tipo) => {
    const raw = base64.includes(",") ? base64.split(",")[1] : base64;
    const byteCharacters = atob(raw);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: tipo });
  };

  const handleVerDocumento = (doc) => {
    if (!doc.base64) return;
    const tipo = doc.tipo || "application/pdf";
    const blob = base64ToBlob(doc.base64, tipo);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-documentos-titulo">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96%", md: "60%", lg: "50%" },
          maxWidth: 750,
          maxHeight: "88vh",
          bgcolor: "background.paper",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            backgroundColor: theme.palette.info?.main || "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Documentacion
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Seleccione documentos
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, maxHeight: "calc(88vh - 140px)", overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Cargando documentos...
              </Typography>
            </Box>
          ) : documentos.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay archivos adjuntos a esta entidad
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.info?.main || "#1976d2", 0.1) }}>
                    <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: "center", width: 80 }}>Ver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentos.map((doc, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: alpha(theme.palette.info?.main || "#1976d2", 0.02) },
                        "&:nth-of-type(even)": { backgroundColor: alpha(theme.palette.grey[50], 0.5) },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ textAlign: "left" }}>{doc.nombre || `Documento ${index + 1}`}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleVerDocumento(doc)}
                          disabled={!doc.base64}
                          sx={{
                            textTransform: "none",
                            minWidth: 50,
                            fontSize: "0.75rem",
                            py: 0.25,
                          }}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalDocumentosAdjuntos;
